import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect, useState, useRef } from "react";
import { Container, Row, Col, Alert } from "react-bootstrap";
import useUserApi, { User, UserEventType } from "../../managers/UserManager";
import OrderList from "../order/component/OrderList";
import UserUsage from "./UserUsage";
import Loading, { LoadingStates } from "../../components/Loading";

const Account: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const { getOrCreateUser } = useUserApi();
  const [backendUser, setBackendUser] = useState<User | null>(null);
  const [backendError, setBackendError] = useState<string | null>(null);
  const hasFetchedUser = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const fetchUser = async () => {
      if (!isMounted) return;
      try {
        if (isAuthenticated && user && !hasFetchedUser.current) {
          const fetchedUser = await getOrCreateUser();
          if (isMounted) {
            setBackendUser(fetchedUser);
            hasFetchedUser.current = true;
          }
        }
      } catch (error) {
        console.error("Error fetching user:", error);
        if (isMounted) {
          setBackendError("Failed to load account info.");
          hasFetchedUser.current = true;
        }
      }
    };

    fetchUser();
    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, user]);

  if (isLoading || !user?.sub || !backendUser) {
    return (
      <Loading state={LoadingStates.LOADING} message="Loading account..." />
    );
  }

  if (!isAuthenticated) return <Alert variant="warning">Please log in to access your account.</Alert>;

  return (
    <Container className="py-4">
      {backendError && <Alert variant="danger">{backendError}</Alert>}

      <Row className="mb-5">
        <Col md="12">
          <h1 className="text-center display-4">My Account</h1>
          <>
            <Row className="align-items-start gy-4">
              {/* Profile image and name */}
              <Col md={6}>
                <h5 className="mt-2">{user?.name || "Unnamed User"}</h5>
                <div className="mb-2"><strong>Login Provider:</strong> {backendUser?.loginProvider}</div>
                <div className="mb-2"><strong>Email:</strong> {backendUser?.email}</div>
                <div className="mb-2"><strong>Created:</strong> {new Date(backendUser.createdDate || "").toLocaleString()}</div>
              </Col>

              {/* Usage Progress */}
              <Col md={6}>
                <UserUsage
                  userId={user.sub}
                  backendUser={backendUser}
                  eventType={UserEventType.AI_GENERATION}
                  frequencies={['daily', 'weekly', 'monthly', 'alltime']}
                />
              </Col>
            </Row>

            <hr />
            <h3 className="text-center">My Orders</h3>
            <OrderList userId={user.sub} />
          </>
        </Col>
      </Row>
    </Container>
  );
};

export default Account;
